import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormTextarea from '../../../components/form/FormTextarea';

import { allowIpAdd, allowIpUpdate } from '../../../store/slices/tenantSlices/settingSlice';

// IPv4, optionally with a CIDR suffix (e.g. 203.0.113.45 or 203.0.113.0/24)
const IPV4_REGEX =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}(\/([0-9]|[1-2]\d|3[0-2]))?$/;

const AddEditAllowIpModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const initialValues = {
    ip: '',
    description: ''
  };

  const validationSchema = Yup.object({
    ip: Yup.string()
      .trim()
      .required('IP address is required')
      .matches(IPV4_REGEX, 'Enter a valid IPv4 address (optionally with /CIDR)'),
    description: Yup.string()
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      const body = { ip: values.ip.trim(), description: values.description };
      const action = isEdit
        ? allowIpUpdate({ id: editRecord.id, ...body })
        : allowIpAdd(body);
      const response = await dispatch(action).unwrap();
      if (response && response.success) {
        toast.success(
          response.message || (isEdit ? 'Record Updated Successfully' : 'Record Added Successfully')
        );
        onCloseClick(true);
        resetForm();
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues({
        ip: editRecord.ip || '',
        description: editRecord.description || ''
      });
    } else {
      resetForm();
    }
  }, [isEdit, editRecord, isOpen]);

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Allow IP' : 'Add Allow IP'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="allow-ip-form" onSubmit={handleSubmit}>
          <Row className="mt-3">
            <Col lg="12">
              <FormInput
                name="ip"
                label="IP Address"
                placeholder="e.g. 203.0.113.45"
                value={values.ip}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col lg="12">
              <FormTextarea
                name="description"
                label="Description"
                placeholder="Enter Description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="allow-ip-form" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditAllowIpModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditAllowIpModal;
